import React, { useState } from "react";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { X } from "lucide-react";

/**
 * SOUND-294 — Small modal for changing a partner's role on a company's
 * sources[]. Submits PATCH /companies/:id/sources/:partnerId with { role }.
 */
const ROLE_OPTIONS = [
  { value: "primary", label: "Primary", description: "The lead partner on this account." },
  { value: "secondary", label: "Secondary", description: "Supporting role alongside a primary partner." },
  { value: "equal", label: "Equal", description: "Shared credit — split between partners." },
];

export default function ChangeRoleModal({
  company,
  source,
  partnerName,
  onClose,
  onSaved,
}) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();

  const [role, setRole] = useState(source?.role || "primary");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!role || role === source.role) {
      onSaved();
      return;
    }
    setSaving(true);
    try {
      await makeApiRequest(
        "patch",
        apiRoutes.updateCompanySource(company.companyId, source.partnerId),
        auth.user,
        effectiveCompanyId,
        { role }
      );
      toast({
        status: "success",
        title: "Role updated",
        description: `${partnerName} is now ${role} on ${company.name}.`,
      });
      onSaved();
    } catch (err) {
      console.error("[ChangeRoleModal] Update failed", err);
      toast({
        status: "destructive",
        title: "Failed to update role",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-md shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Change Role</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Update <span className="text-foreground font-medium">{partnerName}</span>'s role on {company.name}.
            </p>
          </div>
          <button
            className="btn btn-ghost h-8 w-8 p-0"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-1.5 mb-4">
          {ROLE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                role === opt.value
                  ? "border-brand bg-brand/10"
                  : "border-border hover:bg-muted/60"
              }`}
            >
              <input
                type="radio"
                name="change-role"
                value={opt.value}
                checked={role === opt.value}
                onChange={() => setRole(opt.value)}
                className="mt-0.5 h-4 w-4 border-border text-brand focus:ring-brand"
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground">
                  {opt.label}
                </div>
                <div className="text-xs text-muted-foreground">
                  {opt.description}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div className="flex gap-2 pt-2 border-t border-border">
          <button
            className="btn btn-ghost flex-1"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary flex-1"
            onClick={handleSave}
            disabled={saving || role === source?.role}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
